import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { IMAGES, DC_SECTIONS, POWER_INTEL, SCREENING_TOOLS } from "@/data/site";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Gauge, ArrowRight, ScanSearch, Handshake, Mail, Check } from "lucide-react";

export default function DataCenter() {
  useSeo("Data Center Site Development | 12 Stone Development Group",
    "Data center site development starts before the data center. 12 Stone identifies and assembles the land, power, gas, water, wastewater and fiber required for high-demand technology development in Georgia and the Southeast.");
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#partnership-support") {
      const t = setTimeout(() => {
        document.getElementById("partnership-support")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [location.key, location.hash]);
  return (
    <div>
      <PageHero overline="Data Center Development"
        title="Data Center Site Development Starts Before the Data Center"
        subtitle="12 Stone focuses on identifying and assembling the infrastructure required for high-demand technology development — long before a facility is announced."
        image={IMAGES.dataCenter} height="min-h-[68vh]" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <Reveal>
          <Overline>The Foundation</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground max-w-3xl text-balance">
            Hyperscale readiness is an infrastructure problem, not a real estate one
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {DC_SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.05}>
              <div className="rounded-xl border border-border bg-card p-6 h-full hover:border-primary/40 transition-colors">
                <div className="font-mono text-xs text-primary mb-2">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-lg font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap gap-3">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-7"><Link to="/site-submission">SUBMIT A DATA CENTER SITE</Link></Button>
          <Button asChild size="lg" className="bg-transparent hover:bg-secondary text-primary border border-border font-semibold rounded-full px-7"><Link to="/partners">DISCUSS A DEVELOPMENT PARTNERSHIP</Link></Button>
        </div>
      </section>

      {/* POWER INTELLIGENCE */}
      <section className="border-y border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>Power Infrastructure Intelligence</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground text-balance">The site is only as good as the power behind it</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">12 Stone evaluates the full power picture to determine whether a site can realistically deliver large-load capacity.</p>
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {POWER_INTEL.map((p) => (
                <li key={p} className="flex items-center gap-2.5 text-sm text-foreground/80"><span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" /> {p}</li>
              ))}
            </ul>
            <Button asChild size="lg" className="mt-9 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-7" data-testid="launch-power-optimizer">
              <a href="#power-optimizer"><Gauge className="mr-2 h-4 w-4" /> LAUNCH POWER OPTIMIZER</a>
            </Button>
          </Reveal>
          <Reveal delay={0.1}>
            <div id="power-optimizer" className="rounded-xl border border-border bg-card p-8">
              <div className="flex items-center gap-3 mb-4">
                <Gauge className="h-6 w-6 text-primary" />
                <h3 className="font-display text-xl font-semibold text-foreground">Power Optimizer</h3>
              </div>
              <p className="text-sm text-muted-foreground">The Power Optimizer application models MW availability, cooling efficiency, heat-reuse potential and land footprint for data center operators and energy partners.</p>
              <div className="mt-5 rounded-lg border border-dashed border-border bg-background/50 p-5 text-center">
                <p className="font-mono text-xs uppercase tracking-widest text-primary">Integration Coming Soon</p>
                <p className="text-sm text-muted-foreground mt-2">This module is architected to connect directly to the Power Optimizer application.</p>
              </div>
              <Button asChild variant="outline" className="mt-5 w-full border-border"><Link to="/contact">Request early access</Link></Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SCREENING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <Reveal>
          <Overline>Development Site Screening</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground text-balance">Screen a property against what actually matters</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SCREENING_TOOLS.map((t, i) => (
            <Reveal key={t.title} delay={(i % 3) * 0.05}>
              <div className="rounded-xl border border-border bg-card p-6 h-full">
                <ScanSearch className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-display text-lg font-semibold text-foreground">{t.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-7"><Link to="/site-submission">SCREEN A PROPERTY <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
      </section>

      {/* DATA CENTER & MUNICIPAL PARTNERSHIP SUPPORT */}
      <section id="partnership-support" className="scroll-mt-24 border-t border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>Partnership Support</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground text-balance">
              Data Center &amp; Municipal Partnership Support
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              12 Stone Development Group helps data center developers and local municipalities work through
              project discussions and negotiations. We help bring project needs and community priorities to
              the table so both sides can explore a clear, practical path forward.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Project requirements, phasing, and development priorities",
                "Utility and public infrastructure coordination",
                "Community benefits and workforce needs",
                "Potential heat reuse, energy, and sustainability initiatives",
                "Meeting preparation and negotiation facilitation",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-foreground/80">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Check className="h-3 w-3" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-xl border border-border bg-card p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Handshake className="h-6 w-6" />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground">Discuss Your Project</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every project is different. Contact us to discuss the right engagement structure and pricing
                for your project.
              </p>
              <Button asChild size="lg" className="mt-6 w-full bg-primary hover:bg-primary/90 text-white font-semibold rounded-full" data-testid="discuss-project-btn">
                <a href="mailto:thomas@12stonedevelopmentgroup.com?subject=Data%20Center%20Municipal%20Partnership%20Inquiry">
                  <Mail className="mr-2 h-4 w-4" /> Discuss a Project
                </a>
              </Button>
              <p className="mt-5 text-xs text-muted-foreground/80 leading-relaxed">
                12 Stone Development Group provides development advisory and facilitation services. Legal,
                engineering, financial, and other professional matters should be reviewed by appropriately
                qualified professionals.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
